/**
 * GRI Disclosure Index - Master Table
 * Shows all GRI disclosures with status tracking
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Filter, Search, Download, CheckCircle, AlertCircle, XCircle,
  ChevronDown, ChevronUp, FileText, ExternalLink, Edit
} from 'lucide-react';
import GRIDisclosureDetailModal from './GRIDisclosureDetailModal';

const API = process.env.REACT_APP_BACKEND_URL;

const GRIDisclosureIndex = ({ study }) => {
  const [index, setIndex] = useState(null);
  const [filteredDisclosures, setFilteredDisclosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, complete, partial, missing
  const [filterMaterial, setFilterMaterial] = useState('all'); // all, material
  const [filterCategory, setFilterCategory] = useState('all'); // all, Universal, Economic, Environmental, Social
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStandard, setExpandedStandard] = useState(null);
  const [selectedDisclosure, setSelectedDisclosure] = useState(null);

  useEffect(() => {
    if (study?.id) {
      fetchIndex();
    }
  }, [study?.id]);

  useEffect(() => {
    if (index) {
      applyFilters();
    }
  }, [index, filterStatus, filterMaterial, filterCategory, searchTerm]);

  const fetchIndex = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/gri-v2/${study.id}/disclosure-index`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndex(res.data);
    } catch (error) {
      console.error('Error fetching disclosure index:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!index) return;

    let filtered = [...index.disclosures];

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(d => d.status === filterStatus);
    }

    // Material filter
    if (filterMaterial === 'material') {
      filtered = filtered.filter(d => d.material);
    }

    // Category filter
    if (filterCategory !== 'all') {
      if (filterCategory === 'Universal') {
        filtered = filtered.filter(d => index.categories.Universal.includes(d.gri_standard));
      } else if (filterCategory === 'Economic') {
        filtered = filtered.filter(d => index.categories.Economic.includes(d.gri_standard));
      } else if (filterCategory === 'Environmental') {
        filtered = filtered.filter(d => index.categories.Environmental.includes(d.gri_standard));
      } else if (filterCategory === 'Social') {
        filtered = filtered.filter(d => index.categories.Social.includes(d.gri_standard));
      }
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.disclosure_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.gri_standard.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredDisclosures(filtered);
  };

  const getStatusIcon = (status) => {
    if (status === 'complete') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (status === 'partial') return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    return <XCircle className="w-4 h-4 text-red-600" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      complete: 'bg-green-100 text-green-700 border-green-300',
      partial: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      missing: 'bg-red-100 text-red-700 border-red-300'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Group by standard
  const groupedDisclosures = filteredDisclosures.reduce((acc, disclosure) => {
    const standard = disclosure.gri_standard;
    if (!acc[standard]) acc[standard] = [];
    acc[standard].push(disclosure);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading disclosure index...</p>
        </div>
      </div>
    );
  }

  if (!index) {
    return <div className="text-center text-gray-600 py-12">No disclosure index available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">GRI Disclosure Index</h2>
        <p className="text-indigo-100">Master table of all GRI disclosures with status tracking</p>
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{index.statistics.total_disclosures}</div>
            <div className="text-sm text-gray-600">Total Disclosures</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{index.statistics.complete}</div>
            <div className="text-sm text-gray-600">Complete</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{index.statistics.partial}</div>
            <div className="text-sm text-gray-600">Partial</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{index.statistics.missing}</div>
            <div className="text-sm text-gray-600">Missing</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-600">{index.statistics.coverage_percentage}%</div>
            <div className="text-sm text-gray-600">Coverage</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-semibold text-gray-700">Filters:</span>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="complete">Complete Only</option>
            <option value="partial">Partial Only</option>
            <option value="missing">Missing Only</option>
          </select>

          <select
            value={filterMaterial}
            onChange={(e) => setFilterMaterial(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Disclosures</option>
            <option value="material">Material Only</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Categories</option>
            <option value="Universal">Universal Standards</option>
            <option value="Economic">Economic (200)</option>
            <option value="Environmental">Environmental (300)</option>
            <option value="Social">Social (400)</option>
          </select>

          <div className="flex-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search disclosures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-sm"
            />
          </div>

          <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Disclosure Table - Grouped by Standard */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {Object.keys(groupedDisclosures).length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              No disclosures match the current filters
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {Object.entries(groupedDisclosures).map(([standard, disclosures]) => (
                <div key={standard}>
                  {/* Standard Header */}
                  <button
                    onClick={() => setExpandedStandard(expandedStandard === standard ? null : standard)}
                    className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">{standard}</div>
                        <div className="text-sm text-gray-600">{disclosures.length} disclosures</div>
                      </div>
                    </div>
                    {expandedStandard === standard ? 
                      <ChevronUp className="w-5 h-5 text-gray-600" /> : 
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    }
                  </button>

                  {/* Disclosure Rows */}
                  {expandedStandard === standard && (
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Disclosure</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Title</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700">Material</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Data Source</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {disclosures.map((disclosure, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedDisclosure(disclosure)}>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(disclosure.status)}
                                <span className="font-mono text-sm font-semibold text-gray-900">
                                  {disclosure.disclosure_number}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{disclosure.title}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              {disclosure.material ? (
                                <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                                  Yes
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-xs text-gray-600 flex items-center gap-1">
                                {disclosure.data_source}
                                {disclosure.data_source !== 'Manual' && (
                                  <ExternalLink className="w-3 h-3 text-gray-400" />
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {getStatusBadge(disclosure.status)}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDisclosure(disclosure);
                                  }}
                                  className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-1">About the Disclosure Index</p>
          <p>
            This master table shows all GRI disclosures required for your reporting. Use the filters to focus on specific 
            categories or status levels. Data is automatically sourced from SAARV operational modules where available.
            Click on any disclosure row or the edit icon to enter/update data.
          </p>
        </div>
      </div>

      {/* Disclosure Detail Modal */}
      {selectedDisclosure && (
        <GRIDisclosureDetailModal
          disclosure={selectedDisclosure}
          studyId={study.id}
          onClose={() => setSelectedDisclosure(null)}
          onSave={fetchIndex}
        />
      )}
    </div>
  );
};

export default GRIDisclosureIndex;